import { Template } from "components/ScenarioEditor/components/TemplatesEditor";
import { useCallback } from "react";
import { usePromise } from "utils/usePromise";
import { err, ok, unwrapOk } from "utils/types";
import { pick } from "utils/utils";
import { BitrixDeal, Product, ProductScenario, Scenario, UserProfile } from "./dialog";
import {
  FRONTEND_API_DOMAIN,
  BACKEND_API_DOMAIN,
  RESOURCES_DOMAIN,
  API_ACCESS_TOKEN,
  PRIVACY_POLICY_URL,
  FRONTEND_DOMAIN,
  BACKEND_DOMAIN,
  refreshCsrfToken,
  getCsrfToken,
  isBackend,
} from "config";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import i18next from "i18next";
import { getWidgetState } from "utils/useStorage";

export {
  FRONTEND_API_DOMAIN,
  BACKEND_API_DOMAIN,
  RESOURCES_DOMAIN,
  API_ACCESS_TOKEN,
  PRIVACY_POLICY_URL,
};

export const productLink = (slug: string, lang?: string, domain = FRONTEND_DOMAIN) =>
  [domain, lang ? [lang] : [], "product", slug].flat().join("/");

export const AJAX_CONTENT_TYPE = "application/x-www-form-urlencoded";
export const JSON_CONTENT_TYPE = "application/json";
export const CSRF_MISMATCH = "CSRF token mismatch.";
export const CSRF_REPEAT_COUNT = 3;

export class RequestError extends Error {
  constructor(public payload: any) {
    super();
  }
}

export type RequestQueryParams = Record<string, string | number>;
export const getQueryString = (query: RequestQueryParams) => {
  const filtered = Object.fromEntries(
    Object.entries(query).flatMap(([k, v]) => (!!v ? [[k, v.toString()]] : []))
  );
  return new URLSearchParams(filtered).toString();
};

export const apiCall =
  (domain: string) =>
  async (
    endpoint: string,
    options: Omit<RequestInit, "body"> & { body?: any } = {},
    query?: RequestQueryParams
  ) => {
    const queryString = getQueryString(query ?? {});
    const url = domain + endpoint + (queryString ? "?" + queryString : "");
    const headers = new Headers(options.headers);

    if (!headers.has("Content-Type")) headers.set("Content-Type", JSON_CONTENT_TYPE);
    if (options.body && headers.get("Content-Type") === JSON_CONTENT_TYPE)
      options.body = JSON.stringify(options.body);

    const fetchOptions = {
      ...options,
      headers,
    };

    const resp = await fetch(url, fetchOptions);
    const _res = await resp.json().then(ok).catch(err);

    if (_res.type === "err") {
      try {
        const resp_1 = await resp.text();
        const title = new DOMParser()
          .parseFromString(resp_1, "text/html")
          .querySelector("title")?.textContent;
        throw new Error("Recieved not a json response: " + title);
      } catch {
        throw new Error(
          `Failed to parse body as html or json, ${resp.status} ${resp.statusText}`
        );
      }
    } else {
      const body = unwrapOk(_res) as any;

      if (body.message === CSRF_MISMATCH) throw new Error(CSRF_MISMATCH);
      if (body.status === false) {
        if ("errors" in body && Array.isArray(body.errors))
          if (body.errors.length > 0) throw new RequestError(body.errors);
          else
            throw new Error(
              `${resp.status} ${resp.statusText}; status is false; errors empty`
            );

        if (
          "errors" in body &&
          typeof body.errors === "object" &&
          Object.keys(body.errors).length > 0
        )
          throw new RequestError(
            Object.entries(body.errors).map(([k, v]) => `${k}: ${v}`)
          );
        if ("message" in body && Array.isArray(body.message))
          if (body.message.length > 0) throw new RequestError(body.message);
          else throw new Error(`status is false`);
        if ("message" in body) throw new RequestError(body.message);
        if ("data" in body)
          if (Array.isArray(body.data))
            if (body.data.length > 0) throw new RequestError(body.data);
            else throw new Error(`${resp.status} ${resp.statusText}; status is false`);
          else throw new RequestError(body.data);
      }
      if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
      if (body.status === false) throw new Error("failed to call api");
      if (body.status === "error") throw new Error("failed to call api");
      return body;
    }
  };

export const ajaxCall =
  (domain: string) =>
  async (
    endpoint: string,
    options: Omit<RequestInit, "body"> & { body?: any } = {},
    query: Record<string, string> = {}
  ) => {
    const call = apiCall(domain);
    const headers = new Headers(options.headers);

    if (options.body) {
      const filtered = Object.fromEntries(
        Object.entries(options.body).filter(([, v]) => v !== undefined || v !== null)
      );
      options.body = filtered;
    }
    if (!headers.has("Content-Type")) headers.set("Content-Type", AJAX_CONTENT_TYPE);
    if (!headers.has("X-CSRF-TOKEN")) headers.set("X-CSRF-TOKEN", getCsrfToken());
    headers.set("X-Requested-With", "XMLHttpRequest");

    let x = 0;
    while (true) {
      try {
        headers.set("X-CSRF-TOKEN", getCsrfToken());
        const fetchOptions = {
          method: "POST",
          ...options,
          headers,
        };
        let _query = query;

        if (fetchOptions.body) {
          if (fetchOptions.method === "GET") {
            _query = { ..._query, ...fetchOptions.body };
            fetchOptions.body = undefined;
          } else if (headers.get("Content-Type") === AJAX_CONTENT_TYPE)
            fetchOptions.body = new URLSearchParams(
              fetchOptions.body as Record<string, string>
            );
        }

        return await call(endpoint, fetchOptions, query);
      } catch (e: any) {
        ++x;
        if (e.message === CSRF_MISMATCH) {
          if (x >= CSRF_REPEAT_COUNT) throw e;

          const token = await refreshCsrfToken();
          options.body = {
            _token: token,
            ...(options.body ?? {}),
          };
          headers.set("X-CSRF-TOKEN", token);
        } else throw e;
      }
    }
  };

export const frontendAjaxCall = ajaxCall(FRONTEND_DOMAIN);
export const backendAjaxCall = ajaxCall(BACKEND_DOMAIN);

export const frontendAPICall: ReturnType<typeof apiCall> = (endpoint, options, query) =>
  apiCall(FRONTEND_API_DOMAIN)(
    endpoint,
    {
      ...(options ?? {}),
      headers: { token: API_ACCESS_TOKEN, ...(options?.headers ?? {}) },
    },
    query
  );

export const backendAPICall: ReturnType<typeof apiCall> = (endpoint, options, query) =>
  apiCall(BACKEND_API_DOMAIN)(
    endpoint,
    {
      ...(options ?? {}),
      headers: { token: API_ACCESS_TOKEN, ...(options?.headers ?? {}) },
    },
    query
  );

export const getUser = async () => {
  const {
    data: {
      user: {
        detail: { user_id, ...detail },
        email,
      },
    },
    ...rest
  } = await frontendAjaxCall("/web/user", { method: "GET" });

  if (detail.phone && !isPossiblePhoneNumber(detail.phone)) {
    if (isPossiblePhoneNumber("+" + detail.phone)) {
      detail.phone = detail.phone.startsWith("+") ? detail.phone : "+" + detail.phone;
    } else {
      if (detail.phone.length === 10) {
        detail.phone = "38" + detail.phone;
        detail.phone = detail.phone.startsWith("+") ? detail.phone : "+" + detail.phone;
      }
    }
  }

  return { data: { ...detail, id: user_id, email }, ...rest } as {
    data: UserProfile;
    status: string;
    message: string;
  };
};
export const getAuthedUser = async () => {
  return await getUser();
};

export type BitrixContact = {
  phone: string;
  name: string;
  email: string;
  message?: string;
};
export const generateBitrixDeal = ({
  contact,
  deal,
}: {
  contact: BitrixContact;
  deal: BitrixDeal;
}) => {
  if (!contact) console.warn("missing contacts for a lead", deal);
  return frontendAjaxCall("/forms", {
    body: {
      ...contact,
      message: contact?.message ? deal.TITLE + "\n\n" + contact.message : deal.TITLE,
      title: deal.TITLE,
      source_id: "UC_4GBO11",
      category_id: deal.CATEGORY_ID,
      type_id: deal.TYPE_ID,
      type: "11",
      widgetData: JSON.stringify(getWidgetState()),
      widgetCookie: document.cookie,
      siteCookie: window.top?.document.cookie,
    },
  });
};
export const bitrixLeadFromDeal = (deal: BitrixDeal) => (contact: BitrixContact) =>
  generateBitrixDeal({ contact, deal });

export const getProductInfo = (productId: number, lang = "ru") =>
  frontendAPICall("/catalog/product/" + productId, { method: "GET" }, { lang })
    .then(({ data: { scenario, ...rest } }) => {
      const parsed =
        typeof scenario === "string" && scenario !== ""
          ? JSON.parse(scenario)
          : scenario === ""
          ? {
              insurance: { scenario: { initial: [] } },
              incident: { scenario: { initial: [] } },
            }
          : scenario;

      // migration
      if ("insuranceScenario" in parsed) {
        const {
          insuranceScenario,
          incidentScenario,
          insuranceDataConfig,
          incidentDataConfig,
        } = parsed;

        return {
          ...rest,
          insurance: {
            scenario:
              insuranceScenario && "initial" in insuranceScenario
                ? insuranceScenario
                : { initial: [] },
            settings: { bitrixConfig: insuranceDataConfig },
          },
          incident: {
            scenario:
              incidentScenario && "initial" in incidentScenario
                ? incidentScenario
                : { initial: [] },
            settings: { bitrixConfig: incidentDataConfig },
          },
        };
      } else if ("initial" in parsed)
        return {
          ...rest,
          insurance: { scenario: parsed },
          incident: { scenario: { initial: [] } },
        };
      else
        return {
          ...rest,
          ...parsed,
        };
    })
    // .catch(() => ({ ...map[productId], name: "stub" }))
    .then(({ name, image, slug, insurance, incident, relevant, translation }) => {
      return {
        slug,
        insurance,
        incident,
        translation,
        background: !image
          ? undefined
          : RESOURCES_DOMAIN + "/images/catalog/products/" + image,
        title: name ?? "Страхование",
        relevant:
          relevant?.map(({ id, name, description, slug }: any) => ({
            id,
            name,
            description,
            slug,
          })) ?? [],
      } as Product & { slug: string };
    })
    .then((product) => {
      const migrate = (scenario: Scenario) => {
        return Object.fromEntries(
          Object.entries(scenario).map(([branch, scenario]) => {
            return [
              branch,
              scenario.map((block) => {
                return {
                  ...block,
                  dialogBlock: {
                    ...block.dialogBlock,
                    type:
                      block.dialogBlock.blockType === "interactive" &&
                      block.dialogBlock.type === ("file" as string)
                        ? "fileInput"
                        : block.dialogBlock.type,
                  },
                };
              }),
            ];
          })
        ) as Scenario;
      };
      return {
        ...product,
        insurance: {
          ...product.insurance,
          scenario: migrate(product.insurance.scenario),
        },
        incident: {
          ...product.incident,
          scenario: migrate(product.incident.scenario),
        },
      };
    })
    .then((product) => {
      if (!product.incident.runnerVersion) product.incident.runnerVersion = 1;
      if (!product.insurance.runnerVersion) product.insurance.runnerVersion = 1;
      return product;
    });

export const useUpdateProductScenario = (productId?: Number) =>
  useCallback(
    (data: { insurance: ProductScenario; incident: ProductScenario }, lang = "ru") =>
      backendAPICall(
        `/catalog/product/${productId}/update`,
        {
          method: "POST",
          body: { scenario: data },
        },
        { lang }
      ),
    [productId]
  );

// TODO: should migrate all scenario structures (templates and scenario) to { [uniqueId]: { name: string, data: unknown } }
export type HomeData = {
  templates: Template[];
  scenario: Scenario;
};

export const useUpdateHomeData = () =>
  useCallback(
    (scenario: HomeData, lang = "ru") =>
      backendAPICall(
        `/page/update/home`,
        { method: "POST", body: { scenario } },
        { lang }
      ),
    []
  );

// TODO: remove when all rerendering problems are solved
let queried: HomeData | null = null;
let querying = false;
export const useHomeData = (lang = "ru") =>
  usePromise<HomeData>(async () => {
    // TODO: remove that hack, query should only happen once
    while (querying) await new Promise((resolve) => setTimeout(resolve));
    if (queried) return ok(queried);
    querying = true;

    const { data } = await frontendAPICall("/page/home/" + lang, { method: "GET" });
    const { scenario, translation } = data;
    queried = pick(scenario, ["scenario", "templates"]);
    querying = false;

    if (translation && !isBackend)
      i18next.addResourceBundle(lang, "translation", translation);

    return ok(pick(scenario, ["scenario", "templates"]));
  });
